import gql from 'graphql-tag';
import {useMemo} from 'react';

import {
  type ProjectPageQueryVariables,
  useProjectPageQuery,
} from '../../generated/graphql';
import {
  AccessOption,
  type AccessOptions,
  getProjectAccess,
} from '../../util/permissions';

/**
 * This query should only be used in the ProjectPage component.
 * Please only add fields if they are absolutely necessary for the ProjectPage component
 * because it is the main entry point for all our core products.
 *
 * Please avoid adding computeHours, totalRuns, and runCount to this top level query.
 * These are compute heavy and should be lazily fetched as needed for render downstream.
 */
export const PROJECT_PAGE_QUERY = gql`
  query ProjectPage($projectName: String!, $entityName: String) {
    project(name: $projectName, entityName: $entityName) {
      id
      name
      entityName
      access
      hasRun
      fromV2
      entity {
        id
        claimingEntity {
          id
          name
        }
      }
    }
  }
`;

export type ProjectPageProject = {
  id: string;
  name: string;
  entityName: string;
  access: AccessOptions;
  hasRun: boolean;
  entity: {
    claimingEntity: {
      id: string;
      name: string;
    } | null;
  };
};

export type ProjectPageQueryResult = {
  loading: boolean;
  error: Error | undefined;
  project: ProjectPageProject | undefined;
};

export function useProjectPageQueryResult(
  variables: ProjectPageQueryVariables
): ProjectPageQueryResult {
  const {loading, data, error} = useProjectPageQuery({
    variables,
  });

  return useMemo(() => {
    if (loading) {
      return {
        loading,
        error: undefined,
        project: undefined,
      };
    }

    if (error || data == null || data.project == null) {
      return {
        loading: false,
        error,
        project: undefined,
      };
    }

    return {
      loading: false,
      error: undefined,
      project: {
        id: data.project.id,
        name: data.project.name,
        entityName: data.project.entityName,
        access: getProjectAccess(
          (data.project.access as AccessOptions) ?? AccessOption.Private
        ),
        hasRun: !!data.project.hasRun,
        entity: {
          claimingEntity: data.project.entity?.claimingEntity ?? null,
        },
      },
    };
  }, [loading, data, error]);
}
